import { Fragment } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import type { ReceptionTimelineBucket } from "@/lib/statistics-api.service";

/** Phút tính từ nửa đêm — khoá sắp xếp duy nhất cho một ô 30 phút. */
export function slotOf(hour: number, minute: number): number {
	return hour * 60 + minute;
}

function cellKey(date: string, slot: number): string {
	return `${date}|${slot}`;
}

/** 510 → "8:30". Nhãn cột và cả tooltip đều dùng, để hai chỗ không lệch nhau. */
export function formatSlotLabel(slot: number): string {
	const hour = Math.floor(slot / 60);
	const minute = slot % 60;
	return `${hour}:${minute === 0 ? "00" : minute}`;
}

/** "2026-08-12" → "12/08", không qua `Date` để tránh lệch múi giờ trình duyệt. */
export function formatDayLabel(date: string): string {
	const [, month, day] = date.split("-");
	return month && day ? `${day}/${month}` : date;
}

/** Các ô 30 phút thực sự có hồ sơ, sắp tăng dần theo thời gian trong ngày. */
export function activeSlots(buckets: ReceptionTimelineBucket[]): number[] {
	return Array.from(
		new Set(buckets.filter((b) => b.count > 0).map((b) => slotOf(b.hour, b.minute))),
	).sort((a, b) => a - b);
}

export function activeDays(buckets: ReceptionTimelineBucket[]): string[] {
	return Array.from(new Set(buckets.map((b) => b.date))).sort();
}

interface ReceptionHeatmapGridProps {
	buckets: ReceptionTimelineBucket[];
	/**
	 * Trục ép từ ngoài vào. Nhiều lưới đặt cạnh nhau (một lưới mỗi người tiếp
	 * nhận) phải dùng CHUNG một bộ ngày/ô giờ và CHUNG `maxCount`, nếu không mỗi
	 * lưới tự co trục và tự chuẩn hoá màu theo dữ liệu riêng — hai ô đậm như nhau
	 * ở hai lưới lại là hai con số khác hẳn, và mắt so sánh ra kết quả sai.
	 */
	days?: string[];
	slots?: number[];
	maxCount?: number;
	/** Bề rộng cột nhãn ngày; lưới nhỏ cần hẹp hơn lưới tổng. */
	dayLabelWidth?: string;
}

/**
 * Lưới ngày × ô 30 phút, tô đậm theo số hồ sơ. Chỉ phần lưới — không tiêu đề,
 * không chú giải — để dùng lại được cho cả lưới tổng lẫn lưới của từng người.
 *
 * Recharts không có kiểu biểu đồ heatmap, nên đây là một lưới CSS thuần — ép
 * một dữ liệu 2 chiều vào biểu đồ trục X/Y của recharts sẽ tốn công hơn là tự vẽ.
 */
export function ReceptionHeatmapGrid({
	buckets,
	days: daysProp,
	slots: slotsProp,
	maxCount: maxCountProp,
	dayLabelWidth = "4.5rem",
}: ReceptionHeatmapGridProps) {
	const days = daysProp ?? activeDays(buckets);
	const slots = slotsProp ?? activeSlots(buckets);
	const countByCell = new Map(
		buckets.map((b) => [cellKey(b.date, slotOf(b.hour, b.minute)), b.count]),
	);
	const maxCount = maxCountProp ?? Math.max(1, ...buckets.map((b) => b.count));

	return (
		<div className="overflow-x-auto">
			<div
				className="grid min-w-max gap-1"
				style={{
					gridTemplateColumns: `${dayLabelWidth} repeat(${slots.length}, minmax(2.25rem, 1fr))`,
				}}
			>
				<div />
				{slots.map((slot) => (
					<div key={slot} className="text-center text-[11px] text-muted-foreground">
						{formatSlotLabel(slot)}
					</div>
				))}
				{days.map((date) => (
					<Fragment key={date}>
						<div className="flex items-center text-xs text-muted-foreground">
							{formatDayLabel(date)}
						</div>
						{slots.map((slot) => {
							const count = countByCell.get(cellKey(date, slot)) ?? 0;
							// Ngưỡng dưới 0.12 để một ô có dữ liệu vẫn tách được với ô
							// rỗng (0), thay vì nhạt tới mức trông giống hệt nhau.
							const alpha = count === 0 ? 0 : 0.12 + (count / maxCount) * 0.78;
							return (
								<div
									key={slot}
									className="flex aspect-square items-center justify-center rounded-sm text-[10px] font-medium leading-none tabular-nums"
									style={{
										backgroundColor:
											count === 0 ? "hsl(var(--muted))" : `hsl(var(--chart-1) / ${alpha})`,
										// Chữ đổi màu theo độ đậm của NỀN chứ không cố định: một màu
										// duy nhất luôn chìm ở một đầu thang. Ngưỡng 0.55 là chỗ nền
										// đủ đậm để chữ sáng thắng chữ tối.
										color:
											alpha >= 0.55 ? "hsl(var(--background))" : "hsl(var(--foreground) / 0.75)",
									}}
									title={`${formatDayLabel(date)}, ${formatSlotLabel(slot)} (giờ VN): ${count.toLocaleString("vi-VN")} hồ sơ`}
								>
									{/* Ô rỗng để TRỐNG chứ không in "0": lưới thưa hơn phần có dữ
									    liệu rất nhiều, in đủ số 0 thì mắt phải lọc nhiễu trước khi
									    thấy được đợt đông. */}
									{count > 0 && count.toLocaleString("vi-VN")}
								</div>
							);
						})}
					</Fragment>
				))}
			</div>
		</div>
	);
}

/** Chú giải thang màu: nhạt nhất → đậm nhất, gắn với số hồ sơ lớn nhất đang có. */
export function HeatmapLegend({ maxCount }: { maxCount: number }) {
	return (
		<div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
			<span>Ít</span>
			<div className="flex h-3 flex-1 max-w-40 overflow-hidden rounded-sm">
				{[0.12, 0.3, 0.48, 0.66, 0.84, 1].map((alpha) => (
					<div
						key={alpha}
						className="h-full flex-1"
						style={{ backgroundColor: `hsl(var(--chart-1) / ${alpha})` }}
					/>
				))}
			</div>
			<span>Nhiều (tối đa {maxCount.toLocaleString("vi-VN")} hồ sơ/30 phút)</span>
		</div>
	);
}

interface ReceptionHeatmapProps {
	/** `undefined` = lần tải đầu chưa xong, khác mảng rỗng (đã tải, không có gì). */
	buckets: ReceptionTimelineBucket[] | undefined;
	totalReceived: number;
	/** null khi chưa có hồ sơ nào trong phạm vi lọc — không có giờ nào là "đỉnh". */
	peakHour: number | null;
}

/**
 * Lưới nhiệt độ tiếp nhận TỔNG của cả phạm vi đang lọc.
 *
 * Mỗi cột là một ô 30 PHÚT, không phải một giờ: giờ tiếp nhận dồn vào vài khung
 * hành chính, ở bước 1 giờ cả buổi sáng chỉ còn ba bốn ô nên không thấy được
 * đợt đông nằm ở nửa đầu hay nửa sau giờ.
 *
 * Cột CHỈ hiện những ô thực sự có hồ sơ ở ít nhất một ngày trong phạm vi lọc;
 * vẽ đủ 48 ô sẽ để lại rất nhiều cột trắng (giờ hành chính đóng cửa, đêm khuya)
 * làm loãng phần thực sự có dữ liệu.
 */
export function ReceptionHeatmap({ buckets, totalReceived, peakHour }: ReceptionHeatmapProps) {
	if (buckets === undefined) {
		return (
			<ChartCard title="Nhiệt độ tiếp nhận theo ngày × giờ" filename="nhiet-do-tiep-nhan-ngay-gio">
				<div className="space-y-2">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-6 w-full" />
					))}
				</div>
			</ChartCard>
		);
	}

	const days = activeDays(buckets);
	const slots = activeSlots(buckets);
	const maxCount = Math.max(1, ...buckets.map((b) => b.count));

	return (
		<ChartCard
			title="Nhiệt độ tiếp nhận theo ngày × giờ"
			filename="nhiet-do-tiep-nhan-ngay-gio"
			description="Mỗi ô là 30 phút, giờ Việt Nam (UTC+7)"
		>
			{days.length === 0 || slots.length === 0 ? (
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có hồ sơ nào được tiếp nhận trong phạm vi đang lọc.
				</p>
			) : (
				<>
					<ReceptionHeatmapGrid buckets={buckets} days={days} slots={slots} maxCount={maxCount} />
					<HeatmapLegend maxCount={maxCount} />

					<p className="mt-3 text-sm text-muted-foreground">
						Tổng đã tiếp nhận:{" "}
						<span className="font-semibold text-foreground">
							{totalReceived.toLocaleString("vi-VN")}
						</span>
						{" · "}
						Giờ cao điểm:{" "}
						<span className="font-semibold text-foreground">
							{peakHour === null ? "chưa có dữ liệu" : `${peakHour}h (giờ VN)`}
						</span>
					</p>
				</>
			)}
		</ChartCard>
	);
}
